import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, BookOpen, ExternalLink, Loader2 } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import ConsultationDialog from "@/components/ConsultationDialog";
import { getInitials } from "@/lib/image";
import useSEO from "@/lib/useSEO";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacultyPage() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Faculty & Mentors | Educators Institute of Management",
    description:
      "Meet the faculty behind Educators Institute of Management — chartered, CMA & ACCA-qualified mentors who guide every student till they pass and get placed.",
    canonical: typeof window !== "undefined" ? window.location.href : undefined,
    image: SITE.media.teamWithLogo,
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/faculty`);
        if (!cancel) setList((data || []).filter((f) => f.featured));
      } catch {
        // ignore
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="App font-inter text-slate-800">
      <Header onConsult={() => setOpen(true)} />

      {/* Hero */}
      <section className="relative bg-[#002570] text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.18]" style={{ backgroundImage: `url(${SITE.media.classroomA})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002570] via-[#002570]/95 to-[#001a52]/95" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 font-inter text-sm text-[#73c3ff] hover:text-white mb-5" data-testid="faculty-back-link">
            ← Back to home
          </Link>
          <div className="inline-flex items-center gap-2 bg-[#73c3ff]/20 text-[#73c3ff] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase">
            <GraduationCap className="w-3.5 h-3.5" />
            Faculty & Mentors
          </div>
          <h1 className="mt-5 font-rubik text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-balance max-w-4xl mx-auto">
            Mentors who <span className="text-[#73c3ff]">stay till you pass.</span>
          </h1>
          <p className="mt-5 font-inter text-base md:text-lg text-white/85 max-w-2xl mx-auto">
            Every faculty member at Educators is qualified, currently practising, and personally invested in your result.
          </p>
        </div>
      </section>

      <section className="bg-[#f8fafc] py-16 md:py-20 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading faculty…</div>
          ) : list.length === 0 ? (
            <p className="text-center font-inter text-slate-500 py-20">Faculty list will appear here once the institute adds members.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="faculty-grid">
              {list.map((f, i) => (
                <article
                  key={f.id}
                  data-testid={`faculty-card-${i}`}
                  className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-[#002570] to-[#001a52] text-white flex items-center justify-center font-rubik text-xl font-bold shrink-0">
                      {f.photo ? <img src={f.photo} alt={f.name} className="w-full h-full object-cover" /> : <span>{getInitials(f.name)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-rubik text-lg font-bold text-[#0f172a] truncate">{f.name}</p>
                      {f.role && <p className="font-inter text-sm text-[#002570] font-medium mt-0.5 truncate">{f.role}</p>}
                      {f.qualifications && <p className="font-inter text-xs text-slate-500 mt-0.5 truncate">{f.qualifications}</p>}
                    </div>
                  </div>

                  {f.bio && (
                    <p className="mt-5 font-inter text-sm text-slate-600 leading-relaxed line-clamp-4">{f.bio}</p>
                  )}

                  {(f.courses?.length || f.expertise?.length) && (
                    <div className="mt-5 flex flex-wrap gap-1.5">
                      {(f.courses || []).map((c, j) => (
                        <span key={`c-${j}`} className="inline-flex items-center gap-1 bg-[#73c3ff]/15 text-[#002570] text-[11px] font-inter font-semibold px-2.5 py-1 rounded-full">
                          <BookOpen className="w-3 h-3" /> {c}
                        </span>
                      ))}
                      {(f.expertise || []).map((e, j) => (
                        <span key={`e-${j}`} className="bg-slate-100 text-slate-700 text-[11px] font-inter px-2.5 py-1 rounded-full">{e}</span>
                      ))}
                    </div>
                  )}

                  {f.social_link && (
                    <a href={f.social_link} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-[#002570] hover:text-[#001a52]">
                      <ExternalLink className="w-3.5 h-3.5" /> Profile
                    </a>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-rubik text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] text-balance">
            Want to learn directly from <span className="text-[#002570]">our mentors?</span>
          </h2>
          <p className="mt-4 font-inter text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Book a free consultation and we'll match you with the right faculty for your goal.
          </p>
          <button
            onClick={() => setOpen(true)}
            data-testid="faculty-cta-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-7 py-4 font-inter font-medium transition-all hover:shadow-xl"
          >
            Book Free Career Consultation <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
      <ConsultationDialog open={open} onOpenChange={setOpen} source="faculty-page" />
    </div>
  );
}
